import { LoadingSpinner } from "@/components/loading-spinner";
import { useOverview } from "@/http/api-hooks";
import { useInteractionTracking } from "@/lib/mixpanel/mixpanel-hooks";
import { formatDateToMYYYY } from "@/lib/utils";

import { subMonths } from "date-fns";

import { H3 } from "@/components/typography";
import { OverviewResponse } from "@/http/api-types";
import ActiveClients from "./clients/active-clients";
import ChurnedClients from "./clients/churned-clients";

import NewClients from "./new-clients";
import OverviewMetricsCards from "./overview-metrics-cards";
import RevenueOverview from "./revenue-overview";
import RollingRevenueSum from "./rolling-revenue-sum";

export const last12Months = (trailing_start_index = 0) =>
  Array.from({ length: 12 }, (v, i) => {
    const date = subMonths(new Date(), i + 1 + trailing_start_index);
    return formatDateToMYYYY(date);
  });

const calculateClients = (data: OverviewResponse) => {
  const currentMonth = formatDateToMYYYY(new Date());
  const lastMonth = formatDateToMYYYY(subMonths(new Date(), 1));
  const previousMonth = formatDateToMYYYY(subMonths(new Date(), 2));
  const threeMonthsAgo = formatDateToMYYYY(subMonths(new Date(), 3));

  const currentMonthOverviewData = data.clients.find(row => row.month_year === currentMonth);
  const lastMonthOverviewData = data.clients.find(row => row.month_year === lastMonth);
  const previousMonthOverviewData = data.clients.find(row => row.month_year === previousMonth);
  const threeMonthsAgoOverviewData = data.clients.find(row => row.month_year === threeMonthsAgo);

  const activeClientsLastMonth = lastMonthOverviewData?.active_organizations?.length || 0;
  const activeClientsPreviousMonth = previousMonthOverviewData?.active_organizations?.length || 0;

  const newClientsCurrentMonth = currentMonthOverviewData?.new_clients?.length || 0;
  const newClientsLastMonth = lastMonthOverviewData?.new_clients?.length || 0;

  const newClientsHighPerformanceCount = currentMonthOverviewData?.new_clients_high_performance_count || 0;
  const newClientsTotalCount = currentMonthOverviewData?.new_clients_total_count || 0;
  const newClientsHighPerformancePercentage =
    newClientsTotalCount > 0 ? (newClientsHighPerformanceCount / newClientsTotalCount) * 100 : 0;

  const lastMonthActiveClientIds = (lastMonthOverviewData?.active_organizations || []).map(org => org.id);
  const previousMonthActiveClientIds = (previousMonthOverviewData?.active_organizations || []).map(org => org.id);
  const threeMonthsAgoActiveClientIds = (threeMonthsAgoOverviewData?.active_organizations || []).map(org => org.id);

  const churnedLastMonth = previousMonthActiveClientIds.filter(
    clientId => !lastMonthActiveClientIds.includes(clientId),
  ).length;
  const churnedPreviousMonth = threeMonthsAgoActiveClientIds.filter(
    clientId => !previousMonthActiveClientIds.includes(clientId),
  ).length;

  return {
    activeClientsLastMonth,
    activeClientsPreviousMonth,
    newClientsCurrentMonth,
    newClientsLastMonth,
    newClientsHighPerformancePercentage,
    churnedLastMonth,
    churnedPreviousMonth,
  };
};

function Overview() {
  const { data, isLoading, error } = useOverview();

  const { trackFeatureUsage } = useInteractionTracking();

  const handleTabChange = (tabValue: string) => {
    trackFeatureUsage("Tab Navigation", "click", {
      feature_name: `Overview Tab - ${tabValue}`,
      component_name: "overview-tabs",
    });
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div>Ooops, something went wrong...</div>;

  if (!data) return <div>No data available</div>;

  const lastMonthsRevenue = () => {
    const lastMonth = subMonths(new Date(), 1);
    const lastMonthYearString = formatDateToMYYYY(lastMonth);
    return data.revenue.find(e => e.month_year === lastMonthYearString && e.query_period === "all_days")?.revenue || 0;
  };

  const lastMonthsInfrastructureCostShare = () => {
    const lastMonth = subMonths(new Date(), 1);
    const lastMonthYearString = formatDateToMYYYY(lastMonth);
    return (
      data.revenue.find(e => e.month_year === lastMonthYearString && e.query_period === "all_days")
        ?.infrastructure_cost_share || 0
    );
  };

  const previousMonthsRevenue = () => {
    const previousMonth = subMonths(new Date(), 2);
    const previousMonthYearString = formatDateToMYYYY(previousMonth);
    return (
      data.revenue.find(e => e.month_year === previousMonthYearString && e.query_period === "all_days")?.revenue || 0
    );
  };

  const clients = calculateClients(data);

  return (
    <>
      <OverviewMetricsCards
        lastMonthsRevenue={lastMonthsRevenue()}
        previousMonthsRevenue={previousMonthsRevenue()}
        infrastructureCostShare={lastMonthsInfrastructureCostShare()}
        clients={clients}
      />

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <H3>Clients</H3>
        </div>
        <div className="col-span-6">{data && <ActiveClients audienceData={data.clients} type="client" />}</div>
        <div className="col-span-6">{data && <ActiveClients audienceData={data.clients} type="audience" />}</div>
        <div className="col-span-6">{data && <NewClients audienceData={data.clients} clients={clients} />}</div>
        <div className="col-span-6">{data && <ChurnedClients audienceData={data.clients} clients={clients} />}</div>
        <div className="col-span-12">
          <H3>Revenue</H3>
        </div>
        <div className="col-span-6">{data && <RevenueOverview data={data.revenue} />}</div>
        <div className="col-span-6">{data && <RollingRevenueSum data={data.revenue} />}</div>
      </div>
    </>
  );
}

export default Overview;
